import {
  ValidateJSModel,
  PassportModel
} from 'forms/shared/types';
import { isPVQForm } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import { createDateFromObject, getSafeDateTime } from 'helpers/date';
import { passportPattern } from 'constants/patterns';
import { DEFAULT_LATEST } from 'constants/dateLimits';
import { FormDetails } from 'util/singletons';
import { DateBaseValidatorOptions } from '../../base/date/types';
import pvqNameModelValidator from '../pvqName/name';

const pvqPassportModelValidator: ValidateJSModel<PassportModel> = {
  Status: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();

    if (isPVQForm(formDetails.formType) && attributes?.PassportNumberNotApplicable?.applicable === false) {
      return {};
    }
    if (!isPVQForm(formDetails.formType)) { return {}; }

    return {
      presence: true,
      hasValue: { validator: hasYesOrNo },
    };
  },
  Name: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();

    if (isPVQForm(formDetails.formType) && attributes?.PassportNumberNotApplicable?.applicable === false) {
      return {};
    }
    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        model: { validator: pvqNameModelValidator },
      };
    }
    return {};
  },

  Number: (value, attributes, attributeName, options) => {
    const issued = attributes?.Issued;
    const formDetails = FormDetails.getInstance().getData();
    if (isPVQForm(formDetails.formType) && attributes?.PassportNumberNotApplicable?.applicable === false) {
      return {};
    }

    if (isPVQForm(options?.formType)) {
      return {
        presence: true,
        hasValue: {
          validator: {
            length: { is: 9, message: 'error.passport.length' },
            format: { pattern: passportPattern, message: 'error.passport.pattern' },
          },
        },
      };
    }

    if (!isPVQForm(formDetails.formType) || attributes?.PassportNumberNotApplicable?.applicable === false) {
      return {};
    }

    // If issued in 1990 or after, must be 9 characters long
    const issuedDate = getSafeDateTime(issued);

    const dateThreshold = createDateFromObject({ day: 1, month: 1, year: 1990 });
    if (issuedDate.valid && issuedDate.datetime && issuedDate.datetime >= dateThreshold) {
      // @ts-ignore bad typing with length. need to define type for validations
      return {
        presence: true,
        hasValue: {
          validator: {
            length: { is: 9, message: 'error.passport.length' },
            format: { pattern: passportPattern, message: 'error.passport.pattern' },
          },
        },
      };
    }

    return {
      presence: true,
      hasValue: true,
    };
  },
  Issued: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }
    if (!attributes.PassportNumberNotApplicable?.applicable) { return {}; }
    return {
      presence: true,
      date: { earliest: options.applicantBirthdate, latest: DEFAULT_LATEST },
    };
  },
  Expiration: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }
    if (!attributes.PassportNumberNotApplicable?.applicable) { return {}; }

    const issued = attributes?.Issued;
    const dateLimits: DateBaseValidatorOptions = {};

    if (issued) {
      dateLimits.earliest = issued;
      dateLimits.tooEarly = 'error.passport.expiration.min';
      const latestLimit = getSafeDateTime(issued);

      if (latestLimit.valid) {
        latestLimit.datetime = latestLimit.datetime.plus({ years: 10 });
        if (attributes?.Issued?.month === '2' && attributes?.Issued?.day === '29') {
          latestLimit.datetime = latestLimit.datetime.endOf('month');
        }

        dateLimits.latest = latestLimit.datetime;
        dateLimits.tooLate = 'error.passport.expiration.max';
        dateLimits.requireYear = false; // ignore additional year validation
      }
    }

    return { presence: true, date: dateLimits };
  },
  PassportNumberNotApplicable: {},
  PassportNumberNotApplicableExplanation: (value, attributes, attributeName, options) => {
    const formDetails = FormDetails.getInstance().getData();
    if (!isPVQForm(formDetails.formType)) { return {}; }

    return attributes?.PassportNumberNotApplicable?.applicable === false
      ? { presence: true, hasValue: true } : {};
  },
};

export default pvqPassportModelValidator;
